import { Controller, useFormContext } from "react-hook-form";
import { useRef } from "react";

import { Button } from "ui/button";
import { IconButton } from "ui/icon-button";
import toast from "components/partials/toast/toast";

import { uploadThemeAsset } from "hooks/api/use-theme";

import { useCurrentClient } from "hooks/api/use-clients";
import { useThemePreviewContext } from "containers/admin/clients/client/theme-page/contexts/theme-preview-context";

import Icons from "assets";

const BannerSectionContent = () => {
  // context
  const { isEditMode, colors, fonts, typography, button, banner, setBanner } =
    useThemePreviewContext();
  const { control } = useFormContext();
  const client = useCurrentClient();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const isThemeSet = typography && fonts && colors && button && banner;

  // handler for changing banner in form and context
  const handleBannerChange = (
    url: string,
    fileName: string,
    fieldOnChange: (value: string) => void,
    fileNameOnChange: (value: string) => void,
  ) => {
    setBanner((prev) => ({ ...(prev || {}), imageUrl: url, fileName }));
    fieldOnChange(url);
    fileNameOnChange(fileName);
  };

  // handler for uploading banner to azure blob storage
  const handleFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
    fieldOnChange: (value: string) => void,
    fileNameOnChange: (value: string) => void,
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 256 * 1024) {
        toast.error({
          data: {
            title: "Failed to upload file",
            message: "File must be less than 256 KB",
          },
        });
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }

      try {
        const response = await uploadThemeAsset(client.id, file);
        if (!response || !response.url) {
          throw new Error("No response URL received");
        }
        handleBannerChange(response.url, file.name, fieldOnChange, fileNameOnChange);
      } catch (error) {
        toast.error({
          data: {
            title: "Failed to upload file",
            message: "There was an error uploading your image. Please try again.",
          },
        });
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    }
  };

  // Delete handler
  const handleDelete = (
    fieldOnChange: (value: string) => void,
    fileNameOnChange: (value: string) => void,
  ) => {
    handleBannerChange("", "", fieldOnChange, fileNameOnChange);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    isThemeSet && (
      <div className="flex flex-col">
        <Controller
          name="banner.imageUrl"
          control={control}
          render={({ field: imageUrlField }) => (
            <Controller
              name="banner.fileName"
              control={control}
              render={({ field: fileNameField }) => (
                <>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={(e) =>
                      handleFileChange(e, imageUrlField.onChange, fileNameField.onChange)
                    }
                    accept="image/png, image/jpeg, image/gif"
                    className="hidden"
                  />
                  {!imageUrlField.value ? (
                    <Button
                      variant="outlined"
                      size="large"
                      fullWidth
                      disabled={!isEditMode}
                      startIcon={<Icons.LinkIndicator />}
                      onClick={() => fileInputRef.current?.click()}>
                      Upload
                    </Button>
                  ) : (
                    <div className="flex items-center gap-2 w-full pr-2">
                      <span className="flex-1 truncate text-sm">{fileNameField.value}</span>
                      <IconButton
                        aria-label="Delete banner"
                        size="large"
                        variant="text"
                        disabled={!isEditMode}
                        onClick={() =>
                          handleDelete(imageUrlField.onChange, fileNameField.onChange)
                        }>
                        <Icons.Trash />
                      </IconButton>
                    </div>
                  )}
                  <FileSpecifications />
                </>
              )}
            />
          )}
        />
      </div>
    )
  );
};

const FileSpecifications = () => {
  const specifications = [
    { label: "Format:", value: "PNG | JPEG | GIF" },
    { label: "Maximum file size:", value: "256 KB" },
    { label: "Recommended dimensions:", value: "Ensure the width is 500 pixels." },
  ];

  return (
    <dl className="mt-4 text-sm flex flex-col gap-2">
      {specifications.map((spec, index) => (
        <div className="flex flex-col" key={index}>
          <dt className="font-bold text-neutrals-500">{spec.label}</dt>
          <dd>{spec.value}</dd>
        </div>
      ))}
    </dl>
  );
};

export default BannerSectionContent;
